<script setup lang="ts">
	import { MzBanner, MzButton, MzLoader, MzTitle } from '@monizze/monizze-components';
	import { useI18n } from 'vue-i18n';
	import { useRouter } from 'vue-router';
	import { useUserStore } from '@/stores/user.ts';
	import { computed, onBeforeMount } from 'vue';
	import IllustrationCard from '@/components/IllustrationCard.vue';
	import { useOriginRedirect } from '@/composables/useOriginRedirect.ts';

	const { t } = useI18n();
	const router = useRouter();
	const userStore = useUserStore();
	const { forwardedQuery, leaveFlow } = useOriginRedirect();
	const isCustomer = computed(() => userStore.user.mfa_customer && !userStore.user.mfa_forced);
	const isRecovery = computed(() => sessionStorage.getItem('is_recovery'));

	onBeforeMount(async () => {
		await userStore.refreshMfa();
		if (isRecovery.value) {
			handleContinue();
		}
	});

	const handleContinue = () => {
		// Forced MFA requires email first (like onboarding), then a strong method. Optional-MFA customers and
		// forced users who already have email go straight to the strong-method selection.
		const hasEmail = userStore.mfaMethods.some((m) => m.method === 'email');
		if (userStore.user.mfa_forced && !hasEmail) {
			router.replace({ name: 'setup-mfa-email', query: forwardedQuery.value });
			return;
		}
		router.replace({ name: 'setup-mfa-selection', query: forwardedQuery.value });
	};
</script>

<template>
	<div v-if="isRecovery" class="flex justify-center py-10">
		<mz-loader />
	</div>
	<div v-else class="flex flex-col gap-10 w-full">
		<div class="flex flex-col gap-2">
			<mz-title type="h1" :text="t('uzzer.force_mfa.title')" />
			<p>{{ t('uzzer.force_mfa.subtitle') }}</p>
		</div>
		<illustration-card image="/Mfa/profile.svg">
			<mz-title type="h2" :text="t('uzzer.force_mfa.intro.title')" no-margin />
			<mz-banner v-if="isCustomer" variant="warning" :message="t('uzzer.force_mfa.intro_customer.banner')" />
			<div class="flex flex-col gap-4">
				<p>{{ t('uzzer.force_mfa.intro.question') }}</p>
				<ul v-if="isCustomer" class="ps-6 list-disc">
					<li v-for="index in 2" :key="index">
						<p>{{ t(`uzzer.force_mfa.intro_customer.reason_${index}`) }}</p>
					</li>
				</ul>
				<ul v-else class="ps-6 list-disc">
					<li v-for="index in 3" :key="index">
						<i18n-t :keypath="`uzzer.force_mfa.intro.reason_${index}`" tag="p">
							<template #bold>
								<b>{{ t(`uzzer.force_mfa.intro.bold_reason_${index}`) }}</b>
							</template>
						</i18n-t>
					</li>
				</ul>
				<p>{{ t(`uzzer.force_mfa.intro${isCustomer ? '_customer' : ''}.bottom_text`) }}</p>
			</div>
			<div class="flex flex-col md:flex-row gap-2 md:gap-6">
				<mz-button
					class="w-full md:w-auto"
					:label="isCustomer ? t('common.activate') : t('user.mfa.confirm.card.confirm')"
					width="auto"
					@click.prevent="handleContinue"
				/>
				<mz-button
					v-if="isCustomer"
					class="w-full md:w-auto"
					:label="t('common.skip')"
					width="auto"
					variant="minimal"
					@click.prevent="leaveFlow"
				/>
			</div>
		</illustration-card>
	</div>
</template>
